import {
  ReactElement,
  useEffect,
  useState
} from "react"
import {
  ArrowBackRounded,
  DashboardRounded,
  MenuRounded,
  PeopleRounded,
  PrintRounded,
  PersonOffRounded,
  ShoppingCart,
  HelpRounded,
  TranslateRounded,
  SearchRounded
} from "@mui/icons-material"
import {
  AppBar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Typography,
  useScrollTrigger,
  Tooltip
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { useEmbossers } from "../providers/embossers"
import { useAuth } from "../providers/auth"
import useWidth from "../hooks/window"
import { useUsers } from "../providers/users"
import { useTheme } from "../providers/theme"

const drawerWidth = 200

interface Props {
  children: ReactElement
  windowProp?: () => Window
  title: string
  backTo?: boolean
  actions?: ReactElement
}

export default function Navigation({ children, windowProp, title, backTo, actions }: Props) {
  const {currentRole, emailVerified} = useAuth()
  const {embossersCount} = useEmbossers()
  const {unassignedUsersCount} = useUsers()
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()
  const width = useWidth()
  const {mode} = useTheme()

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: width < 600 ? 0 : 40,
    target: windowProp ? windowProp() : undefined,
  })

  const handleDrawerToggle = () => {
    if (width < 600) {
      setMobileOpen(!mobileOpen)
    }
  }

  const drawer = (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{ 
          display: 'flex',
          justifyContent: 'center',
          padding: 4
        }}
      >
        <img 
          src="https://firebasestorage.googleapis.com/v0/b/index-braille.appspot.com/o/logotype%2Flogo.svg?alt=media&token=ac10c622-b14e-4424-8089-92cd91087099"
          alt="Index Braille AB"
        />
      </Box>
      {
        ((currentRole !== undefined && currentRole === "None") || !emailVerified) ?
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleDrawerToggle()
                  navigate('/')
                }}
              >
                <ListItemIcon>
                  <DashboardRounded />
                </ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>
          </List>
        : null
      }
      {
        ((currentRole !== undefined && currentRole === "None") || !emailVerified) &&
          <Divider />
      }
      {
        currentRole !== undefined && (currentRole.includes("User") || currentRole === "Global Administrator") && emailVerified &&
          <>
            <List
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    backgroundColor: 'transparent',
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <p
                    style={{
                      flexGrow: "1"
                    }}
                  >
                    Dashboard
                  </p>
                  <Tooltip title={"Help Dashboard"}>
                    <IconButton
                      sx={{
                        width: "1.5em",
                        height: "1.5em"
                      }}
                      onClick={() => {
                        var language = (navigator as any).language || (navigator as any).userLanguage
                        const url = `https://webhelp.indexbraille.com/distributor/${language === 'sv' ? language : 'en-us'}/index.html?introduction.html`
                        window.open(url, '_blank')
                      }}
                    >
                      <HelpRounded />
                    </IconButton>
                  </Tooltip>
                </ListSubheader>
              }
            >
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleDrawerToggle()
                    navigate('/identify')
                  }}
                >
                  <ListItemIcon>
                    <SearchRounded />
                  </ListItemIcon>
                  <ListItemText primary="Identify Embosser" />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider />
          </>
      }
      {
        currentRole !== undefined && emailVerified && (currentRole.includes("Translator") || currentRole === "Global Administrator") &&
          <>
            <List
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    backgroundColor: 'transparent',
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <p
                    style={{
                      flexGrow: "1"
                    }}
                  >
                    Translator Panel
                  </p>
                  <Tooltip title={"Help Translator Panel"}>
                    <IconButton
                      sx={{
                        width: "1.5em",
                        height: "1.5em"
                      }}
                      onClick={() => {
                        var language = (navigator as any).language || (navigator as any).userLanguage
                        const url = `https://webhelp.indexbraille.com/distributor-translation/${language === 'sv' ? language : 'en-us'}/index.html?translation.html`
                        window.open(url, '_blank')
                      }}
                    >
                      <HelpRounded />
                    </IconButton>
                  </Tooltip>
                </ListSubheader>
              }
            >
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleDrawerToggle()
                    navigate('/translation')
                  }}
                >
                  <ListItemIcon>
                    <TranslateRounded />
                  </ListItemIcon>
                  <ListItemText primary="Translation" />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider />
          </>
      }
      {
        currentRole !== undefined && emailVerified && (currentRole.includes("Remote Support") || currentRole.includes('Administrator')) &&
          <>
            <List
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    backgroundColor: 'transparent',
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <p
                    style={{
                      flexGrow: "1"
                    }}
                  >
                    Service Panel
                  </p>
                  <Tooltip title={"Help Service Panel"}>
                    <IconButton
                      sx={{
                        width: "1.5em",
                        height: "1.5em"
                      }}
                      onClick={() => {
                        var language = (navigator as any).language || (navigator as any).userLanguage
                        const url = `https://webhelp.indexbraille.com/distributor-service/${language === 'sv' ? language : 'en-us'}/index.html?service.html`
                        window.open(url, '_blank')
                      }}
                    >
                      <HelpRounded />
                    </IconButton>
                  </Tooltip>
                </ListSubheader>
              }
            >
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleDrawerToggle()
                    navigate('/embossers')
                  }}
                >
                  <ListItemIcon>
                    <Badge badgeContent={embossersCount} color="primary">
                      <PrintRounded />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary="Remote Support" />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider />
          </>
      }
      {
        currentRole !== undefined && emailVerified && (currentRole.includes("Shop") || currentRole.includes('Administrator')) &&
        <>
          <List
            subheader={
              <ListSubheader
                disableSticky
                sx={{ backgroundColor: 'transparent' }}
              >
                Shop Panel
              </ListSubheader>
            }
          >
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  handleDrawerToggle()
                  navigate('/spareParts')
                }}
              >
                <ListItemIcon>
                  <ShoppingCart />
                </ListItemIcon>
                <ListItemText primary="Spare parts shop" />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
        </>
      }
      {
        currentRole !== undefined && emailVerified && currentRole === "Global Administrator" ?
          <List
            subheader={
              <ListSubheader
                disableSticky
                sx={{ backgroundColor: 'transparent' }}
              >
                Admin Panel
              </ListSubheader>
            }
          >
            <ListItemButton
              onClick={() => {
                handleDrawerToggle()
                navigate('/users')
              }}
            >
              <ListItemIcon>
                <PeopleRounded />
              </ListItemIcon>
              <ListItemText primary="Users" />
            </ListItemButton>

            <ListItemButton
              onClick={() => {
                handleDrawerToggle()
                navigate('/unassigned-users')
              }}
            >
              <ListItemIcon>
                <Badge badgeContent={unassignedUsersCount} color="primary">
                  <PersonOffRounded />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Unassigned Users" />
            </ListItemButton>

          </List>
        : null
      }
    </div>
  )

  const container = windowProp !== undefined ? () => windowProp().document.body : undefined

  useEffect(() => {
    const w = window as any
    if (w) {
      w.scrollTo(0, 0)
    }
  // eslint-disable-next-line
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative'
      }}
    >
      <AppBar
        elevation={trigger ? 4 : 0}
        sx={{
          backgroundColor: trigger ? (mode === 'light' ? '#f6f6f6' : "#252525") : 'transparent',
          color: mode === 'light' ? '#252525' : '#f6f6f6',
          width: `${width < 600 ? '100%' : `calc(100% - ${drawerWidth}px)`}`
        }}
      >
        <Toolbar sx={{ paddingRight: "1em", paddingLeft: ".5em" }} disableGutters >
          {
            backTo ?
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="account-appbar"
                aria-haspopup="true"
                color="inherit"
                edge="end"
                sx={{ margin: 0 }}
                onClick={() => navigate(-1)}
              >
                <ArrowBackRounded />
              </IconButton>
            : width < 600 ?
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ margin: 0 }}
                onClick={handleDrawerToggle}
              >
                <MenuRounded />
              </IconButton>
            : null
          }
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              marginLeft: ".25em"
            }}
          >
            {trigger && title}
          </Typography>
          <div>
            {actions}
          </div>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: {
            sm: drawerWidth
          },
          flexShrink: {
            sm: 0 
          }
        }}
        aria-label="mailbox folders"
      >
        {
          !backTo &&
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: {
                xs: 'block',
                sm: 'none'
              },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth 
              }
            }}
          >
            {drawer}
          </Drawer>
        }

        <Drawer
          variant="permanent"
          sx={{
            display: {
              xs: 'none',
              sm: 'block'
            },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          pb: 2,
          flexGrow: 1,
          width: {
            sm: `calc(100% - ${drawerWidth}px)`
          }
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  )
}
